import type { PolicyDocumentRecord, ResolvedPolicyDocuments } from '@/types/policy';

export const STATIC_PICS_URL = '/files/PICSforDeal-makingPortal.pdf';
export const STATIC_TERMS_URL = '/files/TermsAndConditionsOfServicesForDealMakingPortal.pdf';
export const STATIC_POLICY_VERSION = 'bundled-legacy';

export const STATIC_POLICY_DOCUMENTS: ResolvedPolicyDocuments = {
  policyUrl: STATIC_PICS_URL,
  termsUrl: STATIC_TERMS_URL,
  policyDate: STATIC_POLICY_VERSION,
  termsDate: STATIC_POLICY_VERSION,
};

type PolicyDocumentKind = 'policy' | 'terms';
type PdfUrlValidator = (url: string) => Promise<boolean>;

const documentKind = (record: PolicyDocumentRecord): PolicyDocumentKind | null => {
  const identity = `${record.name} ${record.policy_file?.name || ''}`.toLowerCase();

  if (/\bterms?\b|conditions?/.test(identity)) return 'terms';
  if (/\bpics\b|privacy|personal\s+information/.test(identity)) return 'policy';

  return null;
};

const normalizePolicyUrl = (record: PolicyDocumentRecord): string => {
  const rawUrl = (record.policy_file_url || record.policy_file?.path || '').trim();
  if (!rawUrl) return '';
  if (rawUrl.startsWith('/')) return rawUrl;

  try {
    const parsed = new URL(rawUrl);
    if (['dealmakingstoragerpro.blob.core.windows.net', 'dealmakingstoragedev.blob.core.windows.net'].includes(parsed.hostname)) {
      return `/api${parsed.pathname}${parsed.search}`;
    }
    return parsed.toString();
  } catch {
    return '';
  }
};

export const validatePolicyPdfUrl = async (url: string, fetcher: typeof fetch = fetch): Promise<boolean> => {
  if (!url.trim()) return false;

  try {
    const baseOrigin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
    const parsed = new URL(url, baseOrigin);
    if (parsed.origin !== baseOrigin) return false;

    const response = await fetcher(url, {
      method: 'HEAD',
      credentials: 'same-origin',
    });
    const contentType = response.headers.get('content-type')?.toLowerCase() || '';

    return response.ok && contentType.includes('application/pdf');
  } catch {
    return false;
  }
};

const resolvedDocument = async (
  record: PolicyDocumentRecord | undefined,
  fallbackUrl: string,
  validator: PdfUrlValidator
) => {
  if (!record) return { url: fallbackUrl, date: STATIC_POLICY_VERSION };

  const candidate = normalizePolicyUrl(record);
  if (!candidate) return { url: fallbackUrl, date: STATIC_POLICY_VERSION };

  try {
    if (await validator(candidate)) {
      return { url: candidate, date: record.updated_at || STATIC_POLICY_VERSION };
    }
  } catch {
    // Fall through to the bundled document.
  }

  return { url: fallbackUrl, date: STATIC_POLICY_VERSION };
};

export const resolvePolicyDocuments = async (
  records: PolicyDocumentRecord[],
  validator: PdfUrlValidator = validatePolicyPdfUrl
): Promise<ResolvedPolicyDocuments> => {
  const policyRecord = records.find((record) => documentKind(record) === 'policy');
  const termsRecord = records.find((record) => documentKind(record) === 'terms');
  const [policy, terms] = await Promise.all([
    resolvedDocument(policyRecord, STATIC_PICS_URL, validator),
    resolvedDocument(termsRecord, STATIC_TERMS_URL, validator),
  ]);

  return {
    policyUrl: policy.url,
    termsUrl: terms.url,
    policyDate: policy.date,
    termsDate: terms.date,
  };
};
